import styled from "styled-components";
import { body, text, label, background, spacing } from "@carvana/helios-styles";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Settings, SignOut } from "@carvana/helios-icons";

import {
  flexAlignVerticalContent,
  flexAlignHorizontalContent,
} from "@/styles/mixins";
import TrackedLink from "@/components/TrackedLink";

import GrayLine from "../GrayLine";
import Avatar from "./Avatar";

import "@carvana/helios-styles/fonts.css";

type Props = {
  name: string;
  email: string;
  isOpen: boolean;
  onClick: () => void;
  onSignout: () => void;
};

const UserMenu = ({ name, email, isOpen, onClick, onSignout }: Props) => {
  const { displaySettingsForDapSite } = useFlags();
  const settingsSegmentProps = {
    event: "Settings Viewed"
  };

  return (
    <DropdownMenu>
      <Avatar letter={name[0]} onClick={onClick} />
      {isOpen && (
        <Menu>
          <UserInfo>
            <UsernameText>{name}</UsernameText>
            <EmailText data-qa="email-text">{email}</EmailText>
          </UserInfo>
          <SettingsSection $displaySettings={displaySettingsForDapSite}>
            <GrayLine margin={0} />
            <SettingsLink data-qa="settings" href="/settings" segmentProps={settingsSegmentProps}>
              <Settings />
              Settings
            </SettingsLink>
          </SettingsSection>
          <GrayLine margin={0} />
          <SignOutButton onClick={onSignout}>
            <SignOut color={text.critical} />
            Sign Out
          </SignOutButton>
        </Menu>
      )}
    </DropdownMenu>
  );
};

const DropdownMenu = styled.div`
  position: relative;
`;

const Menu = styled.div`
  ${flexAlignVerticalContent("flex-start", "flex-start")}
  min-width: 375px;
  background: ${background.default};
  position: absolute;
  border-radius: 8px;
  gap: 1rem;
  top: 40px;
  right: 0;
  padding: 1.5rem;
  box-shadow:
    0px 8px 28px rgba(24, 53, 88, 0.04),
    0px 4px 8px rgba(24, 53, 88, 0.07),
    0px -2px 8px rgba(24, 53, 88, 0.03);
`;

const SettingsLink = styled(TrackedLink)`
  ${label.md}
  ${flexAlignHorizontalContent("flex-start", "center")}
  color: ${text.default};
  gap: ${spacing.md};
`;

const SettingsSection = styled.div<{ $displaySettings: boolean }>`
  display: ${(props) => (props.$displaySettings === true ? "flex" : "none")};
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SignOutButton = styled.button`
  ${label.md}
  ${flexAlignHorizontalContent("flex-start", "center")}
  gap: ${spacing.md};
  color: ${text.critical};
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  width: 100%;
`;

const UserInfo = styled.div`
  ${flexAlignVerticalContent("flex-start", "center")}
`;

const UsernameText = styled.p`
  ${label.lg}
  color: ${text.strong};
`;

const EmailText = styled.p`
  ${body.regular.md}
  color: ${text.strong};
`;

export default UserMenu;
